import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Eye, Edit2, Ban, FileText, Printer, Download, Mail } from 'lucide-react';
import '../../styles/erp-theme.css';

/**
 * Per-row action column — three-dot menu opened in a Portal.
 * Each handler is optional; omit a handler to hide its menu item.
 */
export default function ERPActionColumn({
  row,
  onView,
  onEdit,
  onVoid,
  onPreview,
  onPrint,
  onExport,
  onEmail,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      // The dropdown is portaled to document.body, so we MUST exclude it
      // from the outside-click check — otherwise mousedown on any menu item
      // closes the dropdown BEFORE the click event fires (action lost).
      if (btnRef.current && btnRef.current.contains(e.target))  return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: Math.max(4, r.right - 180) });
    }
    setOpen(o => !o);
  };

  const handle = (fn) => (e) => {
    e.stopPropagation();
    setOpen(false);
    fn && fn(row);
  };

  const items = [
    onView    && { key: 'view',    label: 'View',    Icon: Eye,      cls: 'act-view',    onClick: onView },
    onEdit    && { key: 'edit',    label: 'Edit',    Icon: Edit2,    cls: 'act-edit',    onClick: onEdit },
    onPreview && { key: 'preview', label: 'Preview', Icon: FileText, cls: 'act-preview', onClick: onPreview },
    onPrint   && { key: 'print',   label: 'Print',   Icon: Printer,  cls: 'act-print',   onClick: onPrint },
    onExport  && { key: 'export',  label: 'Export',  Icon: Download, cls: 'act-export',  onClick: onExport },
    onEmail   && { key: 'email',   label: 'Email',   Icon: Mail,     cls: 'act-email',   onClick: onEmail },
  ].filter(Boolean);

  return (
    <span ref={btnRef} onClick={(e) => e.stopPropagation()}>
      <button
        className="erp-action-trigger"
        onClick={toggle}
        data-testid={`row-action-trigger`}
        title="Row actions"
      >
        <MoreVertical size={14} />
      </button>
      {open && createPortal(
        <div ref={menuRef} className="erp-action-dropdown" style={{ top: pos.top, left: pos.left }} data-testid="row-action-dropdown">
          {items.map(it => {
            const Icon = it.Icon;
            return (
              <button key={it.key} className={`erp-action-item ${it.cls}`} onClick={handle(it.onClick)}
                data-testid={`row-action-${it.key}`}>
                <Icon size={13} /> <span>{it.label}</span>
              </button>
            );
          })}
          {onVoid && <>
            <div className="erp-action-divider" />
            <button className="erp-action-item act-void danger" onClick={handle(onVoid)}
              data-testid="row-action-void">
              <Ban size={13} /> <span>Void</span>
            </button>
          </>}
        </div>,
        document.body
      )}
    </span>
  );
}
